import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Tabs } from 'components/ui';
import Modal from 'components/ui/Modal';
import Button, { ButtonType } from 'components/ui/Button';

import TypeSignature from './Type';
import SignatureInput from './SignatureInput';

interface SignatureModalProps {
  disabled?: boolean;
  open?: boolean;
  loading?: boolean;
  hideCloseButton?: boolean;
  onClose?: () => void;
  onChange?: (signature?: HTMLCanvasElement) => void;
  onConfirm: () => void;
}

const SignatureModal: React.FC<SignatureModalProps> = ({ disabled, open, onClose, onChange, onConfirm, loading, hideCloseButton }) => {
  const intl = useIntl();

  const [tabIndex, setTabIndex] = React.useState(0);

  const ref = React.useRef<HTMLButtonElement>(null);

  const tabs = React.useMemo(() => [{
    id: 'draw',
    title: intl.formatMessage({ id: 'signature.draw', defaultMessage: 'Draw' }),
    'data-testid': 'signature-tab-draw',
    component: <SignatureInput onChange={onChange} className="h-auto! kiosk:h-96! aspect-video w-full" />
  }, {
    id: 'type',
    title: intl.formatMessage({ id: 'signature.type', defaultMessage: 'Type' }),
    'data-testid': 'signature-tab-type',
    component: <TypeSignature onChange={onChange} />
  }], [intl, onChange]);

  const handleTabChange = React.useCallback((index: number) => {
    onChange?.();
    setTabIndex(index);
  }, [onChange]);

  return (
    <Modal
      open={open}
      focusRef={ref}
      onClose={onClose}
      className="modal-large"
      hideCloseButton={hideCloseButton}
      title={<FormattedMessage id="signature.signHere" defaultMessage="Sign here" tagName="h2" />}
    >
      <div className="flex flex-col items-center mt-5 kiosk:mt-10" data-testid="signatureModal">
        <Tabs selectedIndex={tabIndex} onChange={handleTabChange} tabs={tabs} data-testid="signature-tabs" />
        <div className="flex flex-col w-full kiosk:flex-row-reverse">
          <Button
            ref={ref}
            loading={loading}
            onClick={onConfirm}
            disabled={disabled}
            testId="signatureConfirmButton"
            className="w-full mt-5 mb-3 kiosk:my-0 kiosk:ml-5"
          >
            {tabIndex === 0 && <FormattedMessage id="signature.drawConfirm" defaultMessage="Confirm with drawing" />}
            {tabIndex === 1 && <FormattedMessage id="signature.typeConfirm" defaultMessage="Confirm with typed name" />}
          </Button>
          <Button
            onClick={onClose}
            className="w-full"
            type={ButtonType.SECONDARY}
            testId="cancel-signature-button"
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SignatureModal;
